// ========================
// ADVANCED INVENTORY TYPES
// ========================

export interface StockCategory {
  id: string;
  name: string;
  parentCategory?: string;
  description?: string;
  createdAt: string;
}

export interface StockBatch {
  id: string;
  stockItemId: string;
  batchNumber: string;
  manufacturingDate?: string;
  expiryDate?: string;
  quantity: number;
  rate: number;
  value: number;
  status: "Active" | "Expired" | "Depleted";
  createdAt: string;
}

export type MovementType =
  | "Opening"
  | "Purchase"
  | "Sales"
  | "Adjustment-In"
  | "Adjustment-Out"
  | "Transfer-In"
  | "Transfer-Out"
  | "In"
  | "Out"
  | "Sales Return"
  | "Purchase Return";

export interface StockMovement {
  id: string;
  companyId?: string;
  stockItemId: string;
  voucherId?: string;
  voucherLineId?: string;
  branchId?: string;
  warehouse?: string;
  batchId?: string;
  type: MovementType;
  date: string;
  quantity: number;
  rate: number;
  value: number;
  reference?: string;
  narration?: string;
  createdBy: string;
  createdAt: string;
}

export interface StockTransfer {
  id: string;
  date: string;
  fromItemId: string;
  toItemId: string;
  quantity: number;
  rate: number;
  value: number;
  narration?: string;
  createdBy: string;
  createdAt: string;
}

// ========================
// FIFO BATCH LAYER
// ========================

export interface FIFOLayer {
  batchId?: string;
  date: string;
  quantity: number;
  rate: number;
}

export function fifoConsume(layers: FIFOLayer[], qty: number): { consumed: { layer: FIFOLayer; qty: number; cost: number }[]; remaining: FIFOLayer[] } {
  let toConsume = qty;
  const consumed: { layer: FIFOLayer; qty: number; cost: number }[] = [];
  const remaining: FIFOLayer[] = [];

  for (const layer of layers) {
    if (toConsume <= 0) {
      remaining.push({ ...layer });
      continue;
    }
    const take = Math.min(layer.quantity, toConsume);
    consumed.push({ layer, qty: take, cost: take * layer.rate });
    toConsume -= take;
    if (layer.quantity > take) {
      remaining.push({ ...layer, quantity: layer.quantity - take });
    }
  }

  return { consumed, remaining };
}

export function fifoValuation(layers: FIFOLayer[]): number {
  return layers.reduce((sum, l) => sum + l.quantity * l.rate, 0);
}

export function fifoTotalQty(layers: FIFOLayer[]): number {
  return layers.reduce((sum, l) => sum + l.quantity, 0);
}

export function fifoWeightedAvgRate(layers: FIFOLayer[]): number {
  const totalQty = fifoTotalQty(layers);
  if (totalQty === 0) return 0;
  return fifoValuation(layers) / totalQty;
}
